/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  LinearProgress,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import * as orgAdmin from "../../api/orgAdmin";
import { formatBytes } from "../apps/drive/format";

const Card = ({ title, children }) => {
  const theme = useTheme();
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.5, borderRadius: "12px", backgroundColor: theme.palette.secondary.main }}
    >
      <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 1.5 }}>{title}</Typography>
      {children}
    </Paper>
  );
};

const AdminDashboard = () => {
  const theme = useTheme();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    orgAdmin
      .getDashboard()
      .then((data) => setState({ loading: false, data, error: null }))
      .catch((err) =>
        setState({ loading: false, data: null, error: err.message || "Could not load the dashboard." })
      );
  }, []);

  if (state.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "accent.main" }} />
      </Box>
    );
  }
  if (state.error) {
    return <Box sx={{ p: 3 }}><Alert severity="error">{state.error}</Alert></Box>;
  }

  const { storage, members, teams_by_usage: teams, members_by_usage: topMembers } = state.data;
  // There is no purchased-quota concept in the API yet, so the bar is scaled
  // against a nominal 1 TB rather than inventing a plan limit.
  const NOMINAL_QUOTA = 1024 ** 4;
  const pct = Math.min(100, (storage.bytes_used / NOMINAL_QUOTA) * 100);

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2.5 }}>
        <Card title="Members">
          <Typography sx={{ fontSize: 30, fontWeight: 600 }}>{members.total}</Typography>
          <Typography sx={{ fontSize: 13, color: theme.palette.secondaryText.color }}>
            {members.admins} admin{members.admins === 1 ? "" : "s"}
          </Typography>
        </Card>
        <Card title="Files">
          <Typography sx={{ fontSize: 30, fontWeight: 600 }}>{storage.file_count}</Typography>
          <Typography sx={{ fontSize: 13, color: theme.palette.secondaryText.color }}>
            across the organisation
          </Typography>
        </Card>
        <Card title="Team folders">
          <Typography sx={{ fontSize: 30, fontWeight: 600 }}>{teams.length}</Typography>
          <Typography sx={{ fontSize: 13, color: theme.palette.secondaryText.color }}>
            owned by teams, not people
          </Typography>
        </Card>
      </Box>

      <Card title="Storage used">
        <Typography sx={{ fontSize: 30, fontWeight: 600 }}>
          {formatBytes(storage.bytes_used)}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            mt: 1.5,
            height: 8,
            borderRadius: 4,
            "& .MuiLinearProgress-bar": { backgroundColor: "accent.solid" },
          }}
        />
        <Typography sx={{ fontSize: 12, color: theme.palette.secondaryText.color, mt: 1 }}>
          No storage plan is configured in the API yet, so this is shown against a nominal 1 TB.
        </Typography>
      </Card>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
        <Card title="Team folders by usage">
          {teams.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: theme.palette.secondaryText.color }}>
              No team folders yet.
            </Typography>
          ) : (
            teams.map((t) => (
              <Box key={t.id} sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 1 }}>
                <FolderSharedOutlinedIcon sx={{ fontSize: 18, color: "accent.main" }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5 }} noWrap>{t.team_name}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: theme.palette.secondaryText.color }}>
                    {t.member_count} member{t.member_count === 1 ? "" : "s"}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 13 }}>{formatBytes(t.bytes_used)}</Typography>
              </Box>
            ))
          )}
        </Card>

        <Card title="Members by usage">
          {topMembers.map((m) => (
            <Box key={m.user_id} sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 1 }}>
              <PersonOutlineIcon sx={{ fontSize: 18, color: theme.palette.secondaryText.color }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5 }} noWrap>
                  {m.user_full_name || m.user_email || m.user_id}
                </Typography>
                {m.user_full_name && (
                  <Typography sx={{ fontSize: 11.5, color: theme.palette.secondaryText.color }} noWrap>
                    {m.user_email}
                  </Typography>
                )}
              </Box>
              <Typography sx={{ fontSize: 13 }}>{formatBytes(m.bytes_used)}</Typography>
            </Box>
          ))}
        </Card>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
